import { useEffect, useState } from "react";
import { Boxes, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getAdminStock, setAdminStock } from "@/lib/api";

const StockRow = ({ item, onSaved }) => {
  const [val, setVal] = useState(String(item.qty));
  const dirty = Number(val) !== item.qty;

  const save = async () => {
    const qty = Math.max(0, parseInt(val, 10) || 0);
    try {
      await setAdminStock(item.id, qty);
      onSaved(item.id, qty);
      setVal(String(qty));
      toast.success(`${item.name} stock set to ${qty}`);
    } catch {
      toast.error("Could not update stock");
    }
  };

  const bump = (d) => setVal((v) => String(Math.max(0, (parseInt(v, 10) || 0) + d)));

  return (
    <div data-testid={`stock-row-${item.id}`} className="flex flex-wrap items-center gap-2 sm:gap-3 rounded-lg border border-slate-100 p-3">
      <img src={item.image} alt="" className="h-10 w-10 rounded-md object-cover" />
      <div className="min-w-[120px] flex-1">
        <p className="truncate text-sm font-medium text-slate-900">{item.name}</p>
        <p className={`text-xs ${item.qty === 0 ? "text-rose-600" : "text-slate-500"}`}>{item.qty === 0 ? "Sold out" : `${item.qty} in stock`}</p>
      </div>
      <div className="flex items-center gap-1.5">
      <button onClick={() => bump(-1)} data-testid={`stock-dec-${item.id}`} className="grid h-8 w-8 place-items-center rounded-md text-slate-600 hover:bg-slate-100"><Minus className="h-4 w-4" /></button>
      <Input data-testid={`stock-input-${item.id}`} value={val} onChange={(e) => setVal(e.target.value.replace(/\D/g, ""))} className="h-8 w-14 border-slate-200 bg-white text-center" inputMode="numeric" />
      <button onClick={() => bump(1)} data-testid={`stock-inc-${item.id}`} className="grid h-8 w-8 place-items-center rounded-md text-slate-600 hover:bg-slate-100"><Plus className="h-4 w-4" /></button>
      <Button size="sm" disabled={!dirty} onClick={save} data-testid={`stock-save-${item.id}`} className="h-8 bg-blue-600 text-white hover:bg-blue-700">Save</Button>
      </div>
    </div>
  );
};

export const StockPanel = () => {
  const [items, setItems] = useState([]);

  useEffect(() => { getAdminStock().then(setItems).catch(() => {}); }, []);

  const onSaved = (id, qty) => setItems((xs) => xs.map((x) => (x.id === id ? { ...x, qty } : x)));

  return (
    <section data-testid="stock-panel" className="mt-6 rounded-xl bg-white p-5 ring-1 ring-slate-100">
      <div className="mb-3 flex items-center gap-2">
        <Boxes className="h-4 w-4 text-blue-600" />
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Stock</p>
        <span className="text-xs text-slate-400">· goes down automatically when an order is paid (Research Trio uses 1 of each)</span>
      </div>
      <div className="grid gap-2 lg:grid-cols-3">
        {items.map((it) => <StockRow key={it.id} item={it} onSaved={onSaved} />)}
      </div>
    </section>
  );
};

export default StockPanel;
